/**
 * feat-259 AC13 / AT14 — the publish-time empty guard.
 *
 * Because incomplete items now persist, switching a lesson or a drill ON while
 * it contains an empty item shows a confirmation naming them. It WARNS; it does
 * not block. This is the only guard standing between an empty item and a
 * learner (spec §Risky-change flags), and it is advisory by design.
 *
 * ONE helper, called from both live toggle sites. Two inline copies is how this
 * ships with the lesson guarded and the drill not — and AT14 says "a lesson OR
 * drill", so a single-surface test discharges half of it
 * (design/NOTES.md §5).
 *
 * SCOPE, ruled by `design` and recorded here so it is not widened by accident:
 * the guard covers EMPTY PRIMARY CONTENT only, never "incomplete". A fill-blank
 * with text but no `___` marker, or a rehearsal with a half-filled criterion,
 * has content and stays an advisory. Widening it to every advisory would make it
 * fire on most real publishes, and a dialog that always fires is a dialog nobody
 * reads. The wider net would be a spec change.
 */

import type { QuizLesson, QuizUnit } from '../../../services/quizContentAdminService';

export type EmptyItemKind = 'multiple_choice' | 'fill_blank' | 'rehearsal' | 'video' | 'reading';

export interface EmptyItem {
  /** Stable key for React, and the id the editor could navigate to. */
  id: string;
  kind: EmptyItemKind;
  /** Positional, e.g. "Question 3". An empty item HAS NO NAME — a title-based
   *  label would render as an empty row, which is the bug the dialog is warning
   *  about, reproduced inside the warning. */
  label: string;
  /** Why it counts as empty, in the editor's words: "no question text". */
  reason: string;
}

/** One drill's worth of empties, for the grouped lesson dialog. */
export interface EmptyItemGroup {
  drillId: string;
  drillTitle: string;
  items: EmptyItem[];
}

export const PUBLISH_GUARD_COPY = {
  title: (containerTitle: string) => `Turn on “${containerTitle || '(untitled)'}”?`,
  /** "2 of its 7 items are empty." / "1 of its 4 items is empty."
   *  Three agreements in two sentences — pluralise from the COUNT, never by
   *  hand-writing two strings that then drift. Note the two counts govern
   *  different words: `items` follows the TOTAL, `is/are` follows the empties. */
  lede: (empty: number, total: number, drills?: number) => {
    const noun = total === 1 ? 'item' : 'items';
    const verb = empty === 1 ? 'is' : 'are';
    const across = drills && drills > 1 ? `, across ${drills} drills` : '';
    return `${empty} of its ${total} ${noun} ${verb} empty${across}.`;
  },
  consequence: (empty: number) =>
    empty === 1 ? 'Learners will see it blank.' : 'Learners will see them blank.',
  cancel: 'Cancel',
  confirm: 'Turn on anyway',
  /** Row reasons — design/publish-guard.html §"What counts as empty". */
  noQuestionText: 'no question text',
  noBody: 'no body',
  noTitle: 'no title',
  noScenario: 'no scenario',
  noName: 'no name',
} as const;

export const EMPTY_ITEM_BADGE: Record<EmptyItemKind, { label: string; className: string }> = {
  multiple_choice: { label: 'MC', className: 'bg-blue-50 text-blue-700' },
  fill_blank: { label: 'FILL', className: 'bg-preview-light text-preview' },
  reading: { label: 'READING', className: 'bg-reading-light text-reading' },
  video: { label: 'VIDEO', className: 'bg-video-light text-video' },
  rehearsal: { label: 'REHEARSAL', className: 'bg-coral-light text-coral' },
};

/** TRIM BEFORE TESTING. A field holding a single space is empty to a learner,
 *  and the backend now persists `''` and `' '` alike (AT15/AT21). A guard that
 *  only catches `=== ''` is one keystroke from silent. */
const blank = (s: string | null | undefined): boolean => (s ?? '').trim() === '';

/** Per-type ordering that matches the middle column's. `interleaveItems` sorts
 *  by `sort_order`, then type rank, then id — so WITHIN one type the sequence is
 *  `(sort_order, id)`, which is what this reproduces. Deliberately not imported
 *  from `QuizContentManagement`: that module imports this one. */
function inPanelOrder<T extends { id: string; sort_order: number }>(list: T[] | undefined): T[] {
  return [...(list ?? [])].sort((a, b) =>
    a.sort_order !== b.sort_order ? a.sort_order - b.sort_order : a.id.localeCompare(b.id),
  );
}

/** How many items the drill holds, of any kind — the DENOMINATOR the lede
 *  quotes. Do the arithmetic the copy invites: it must be the real total for
 *  THIS container. */
export function countItems(drill: QuizLesson): number {
  return (
    (drill.question_items?.length ?? 0) +
    (drill.rehearsal_items?.length ?? 0) +
    (drill.video_items?.length ?? 0) +
    (drill.reading_items?.length ?? 0)
  );
}

export function emptyItemsInDrill(drill: QuizLesson): EmptyItem[] {
  const out: EmptyItem[] = [];

  inPanelOrder(drill.question_items).forEach((q, i) => {
    if (!blank(q.question_text)) return;
    out.push({
      id: q.id,
      kind: q.question_type === 'fill_blank' ? 'fill_blank' : 'multiple_choice',
      label: `Question ${i + 1}`,
      reason: PUBLISH_GUARD_COPY.noQuestionText,
    });
  });

  inPanelOrder(drill.rehearsal_items).forEach((r, i) => {
    // A rehearsal is empty if EITHER its name or its scenario is blank — both
    // are primary content, and the row says which one is missing.
    const noName = blank(r.name);
    const noScenario = blank(r.scenario_text);
    if (!noName && !noScenario) return;
    out.push({
      id: r.id,
      kind: 'rehearsal',
      label: `Rehearsal ${i + 1}`,
      reason: noScenario ? PUBLISH_GUARD_COPY.noScenario : PUBLISH_GUARD_COPY.noName,
    });
  });

  // Video is NOT empty for lacking a file: it cannot exist without
  // `source_s3_key`, so the only thing it can be missing is its title.
  inPanelOrder(drill.video_items).forEach((v, i) => {
    if (!blank(v.title)) return;
    out.push({ id: v.id, kind: 'video', label: `Video ${i + 1}`, reason: PUBLISH_GUARD_COPY.noTitle });
  });

  inPanelOrder(drill.reading_items).forEach((rd, i) => {
    if (!blank(rd.body)) return;
    out.push({ id: rd.id, kind: 'reading', label: `Reading ${i + 1}`, reason: PUBLISH_GUARD_COPY.noBody });
  });

  return out;
}

export interface PublishGuardReport {
  /** Every empty item, grouped by drill. A lesson's items live in its drills, so
   *  an ungrouped list is unusable when three drills each have a "Question 3". */
  groups: EmptyItemGroup[];
  emptyCount: number;
  totalCount: number;
  /** How many drills contributed empties — the lede's "across 3 drills". */
  drillsWithEmpties: number;
}

export function reportForDrill(drill: QuizLesson): PublishGuardReport {
  const items = emptyItemsInDrill(drill);
  return {
    groups: items.length ? [{ drillId: drill.id, drillTitle: drill.title, items }] : [],
    emptyCount: items.length,
    totalCount: countItems(drill),
    drillsWithEmpties: items.length ? 1 : 0,
  };
}

export function reportForUnit(unit: QuizUnit): PublishGuardReport {
  const groups: EmptyItemGroup[] = [];
  let totalCount = 0;
  (unit.drills ?? []).forEach((d) => {
    totalCount += countItems(d);
    const items = emptyItemsInDrill(d);
    if (items.length) groups.push({ drillId: d.id, drillTitle: d.title, items });
  });
  return {
    groups,
    emptyCount: groups.reduce((n, g) => n + g.items.length, 0),
    totalCount,
    drillsWithEmpties: groups.length,
  };
}

/**
 * The single question both toggle sites ask. `false` means publish straight
 * through with no dialog — which covers BOTH of AT14's negative cases: a
 * container with nothing empty, and a container with no items at all. Zero empty
 * items is zero empty items; "you are publishing an empty drill" is a different
 * warning this feature was not asked for.
 *
 * Turning something OFF is never guarded — pulling content away from learners is
 * always safe and never needs a permission slip.
 */
export function needsPublishConfirmation(report: PublishGuardReport): boolean {
  return report.emptyCount > 0;
}
